export interface LEDScoreBarConfig {
  x: number;
  y: number;
  width: number;
  height: number;
  maxScore: number;
  beadSize: number;
}

interface AnimatingBead {
  index: number;
  startY: number;
  targetY: number;
  startTime: number;
  lightUpDuration: number; // Time to spend lighting up in place
  fallDuration: number;    // Time to spend falling down
  phase: 'lighting' | 'falling'; // Current animation phase
  updateScore?: boolean;   // Whether this animation should update the score when complete
}

export class LEDScoreBar {
  private config: LEDScoreBarConfig;
  private score: number = 0;
  private targetScore: number = 0;
  private animatingBeads: AnimatingBead[] = [];

  constructor(config: LEDScoreBarConfig) {
    this.config = config;
  }

  setScore(score: number) {
    const newScore = Math.max(0, Math.min(score, this.config.maxScore));
    if (newScore > this.score) {
      // Start animation for new beads
      this.startBeadAnimation(this.score, newScore);
    }
    this.targetScore = newScore;
    if (newScore < this.score) {
      // Immediate update for score decreases
      this.score = newScore;
      this.animatingBeads = [];
    }
  }

  /**
   * 🌊 RIPPLE EFFECT - Animate all beads lighting up then settling back down
   */
  triggerRipple() {
    const { height, beadSize } = this.config;
    const bottomY = this.config.y + height - beadSize/2 - 4;

    // Clear existing animations
    this.animatingBeads = [];

    // Animate all current score beads
    for (let i = 0; i < this.score; i++) {
      const targetY = bottomY - (i * beadSize * 1.1);

      this.animatingBeads.push({
        index: i,
        startY: targetY,
        targetY: targetY,
        startTime: performance.now() + (i * 50), // Stagger by 50ms each
        lightUpDuration: 300,  // Flash bright for 300ms
        fallDuration: 200,     // Quick settle back
        phase: 'lighting',
        updateScore: false     // Regular ripple doesn't change score
      });
    }
  }

  /**
   * 🌊 GAME START RIPPLE - Animate all beads (scored and unscored) lighting up from bottom to top
   */
  triggerGameStartRipple() {
    const { height, beadSize, maxScore } = this.config;
    const bottomY = this.config.y + height - beadSize/2 - 4;

    // Clear existing animations
    this.animatingBeads = [];

    // Animate ALL beads from bottom to top (both scored and unscored)
    for (let i = 0; i < maxScore; i++) {
      let currentY: number;

      if (i < this.score) {
        // Scored beads: use their normal stacked position
        currentY = bottomY - (i * beadSize * 1.1);
      } else {
        // Unscored beads: calculate their top section position
        const unscoredIndex = i - this.score;
        const totalUnscored = maxScore - this.score;
        const topSpacing = Math.min(beadSize * 1.2, (height - (this.score * beadSize * 1.1) - 20) / totalUnscored);
        currentY = this.config.y + 10 + (unscoredIndex * topSpacing);
      }

      this.animatingBeads.push({
        index: i,
        startY: currentY,
        targetY: currentY, // Stay in same position, just light up
        startTime: performance.now() + (i * 80), // Stagger by 80ms each for wave effect
        lightUpDuration: 500,  // Flash bright for 500ms
        fallDuration: 300,     // Quick settle back
        phase: 'lighting',
        updateScore: false     // Game start ripple doesn't change score
      });
    }
  }

  private startBeadAnimation(oldScore: number, newScore: number) {
    const { height, beadSize } = this.config;
    const bottomY = this.config.y + height - beadSize/2 - 4;

    for (let i = oldScore; i < newScore; i++) {
      const targetY = bottomY - (i * beadSize * 1.1);

      // Calculate the unscored position for this bead - it should be the bottommost unscored bead
      // We need to find where this bead currently sits in the unscored section
      const totalUnscored = this.config.maxScore - oldScore; // Total unscored before this animation
      const beadUnscoredIndex = i - oldScore; // Which unscored bead this represents (0 = bottommost)
      const reversedIndex = totalUnscored - 1 - beadUnscoredIndex; // Bottom to top indexing

      const topSpacing = Math.min(beadSize * 1.2, (height - (oldScore * beadSize * 1.1) - 20) / totalUnscored);
      const startY = this.config.y + 10 + (reversedIndex * topSpacing);

      this.animatingBeads.push({
        index: i,
        startY: startY,
        targetY: targetY,
        startTime: performance.now() + (i - oldScore) * 150, // Stagger animations more
        lightUpDuration: 300, // Light up for 300ms
        fallDuration: 600,    // Then fall for 600ms (slower)
        phase: 'lighting',
        updateScore: true     // Scoring animation should update score
      });
    }
  }

  getScore(): number {
    return this.score;
  }

  draw(ctx: CanvasRenderingContext2D) {
    this.updateAnimations();
    this.drawThinLine(ctx);
    this.drawBeads(ctx);
  }

  private updateAnimations() {
    const now = performance.now();

    // Update animating beads
    for (let i = this.animatingBeads.length - 1; i >= 0; i--) {
      const bead = this.animatingBeads[i];
      const elapsed = now - bead.startTime;

      if (bead.phase === 'lighting') {
        // Check if lighting phase is complete
        if (elapsed >= bead.lightUpDuration) {
          bead.phase = 'falling';
          bead.startTime = now; // Reset start time for falling phase
        }
      } else if (bead.phase === 'falling') {
        // Check if falling phase is complete
        if (elapsed >= bead.fallDuration) {
          // Animation complete
          this.animatingBeads.splice(i, 1);
          // Only update score if this animation is supposed to update the score
          if (bead.updateScore) {
            this.score = Math.max(this.score, bead.index + 1);
          }
        }
      }
    }

    // Check if all animations are complete
    if (this.animatingBeads.length === 0 && this.score < this.targetScore) {
      this.score = this.targetScore;
    }
  }

  private drawThinLine(ctx: CanvasRenderingContext2D) {
    const { x, y, width, height } = this.config;
    const centerX = x + width / 2;

    ctx.save();

    // Draw very thin vertical line (like metro map)
    ctx.strokeStyle = '#C0C0C0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(centerX, y);
    ctx.lineTo(centerX, y + height);
    ctx.stroke();

    ctx.restore();
  }

  private drawBeads(ctx: CanvasRenderingContext2D) {
    const { x, y, width, height, maxScore, beadSize } = this.config;
    const centerX = x + width / 2;
    const beadRadius = beadSize / 2;
    const now = performance.now();

    // Calculate positions for stacked beads from bottom
    const bottomY = y + height - beadRadius - 4; // Leave small margin at bottom

    // Draw scored beads (static)
    for (let i = 0; i < this.score; i++) {
      const isFifthBead = (i + 1) % 5 === 0;
      const beadY = bottomY - (i * beadSize * 1.1);
      this.drawBead(ctx, centerX, beadY, true, isFifthBead);
    }

    // Draw animating beads
    for (const animBead of this.animatingBeads) {
      if (now >= animBead.startTime) {
        const elapsed = now - animBead.startTime;
        const isFifthBead = (animBead.index + 1) % 5 === 0;

        if (animBead.phase === 'lighting') {
          // Lighting phase: bead stays in unscored position but lights up
          this.drawBead(ctx, centerX, animBead.startY, true, isFifthBead);
        } else if (animBead.phase === 'falling') {
          // Falling phase: bead moves from start to target position
          const progress = Math.min(elapsed / animBead.fallDuration, 1);

          // Easing function for smooth falling animation (ease-out)
          const easedProgress = 1 - Math.pow(1 - progress, 3);

          const currentY = animBead.startY + (animBead.targetY - animBead.startY) * easedProgress;
          this.drawBead(ctx, centerX, currentY, true, isFifthBead);
        }
      }
    }

    // Draw unscored beads at the top (excluding those currently animating)
    // Use current score (not targetScore) to determine which beads are truly unscored
    const currentUnscoredCount = maxScore - this.score;
    const animatingIndices = new Set(this.animatingBeads.map(b => b.index));

    // Calculate remaining unscored beads after removing animating ones
    const remainingUnscored = [];
    for (let i = 0; i < currentUnscoredCount; i++) {
      const beadIndex = this.score + i;
      if (!animatingIndices.has(beadIndex)) {
        remainingUnscored.push(beadIndex);
      }
    }

    // Draw the remaining unscored beads
    const actualUnscoredCount = remainingUnscored.length;
    if (actualUnscoredCount > 0) {
      const topSpacing = Math.min(beadSize * 1.2, (height - (this.score * beadSize * 1.1) - 20) / actualUnscoredCount);

      for (let i = 0; i < remainingUnscored.length; i++) {
        const beadY = y + 10 + (i * topSpacing);
        this.drawBead(ctx, centerX, beadY, false, false); // All unscored beads look the same
      }
    }
  }

  private drawBead(ctx: CanvasRenderingContext2D, x: number, y: number, isScored: boolean, isFifthBead: boolean) {
    const { beadSize } = this.config;
    const radius = beadSize / 2;

    ctx.save();

    if (isScored) {
      // Create soft/fuzzy glow effect for scored beads
      const glowRadius = radius * 2.0;
      const glowColor = isFifthBead ? '#4A9EFF' : '#FFD700';

      // Draw multiple concentric circles for soft glow
      for (let i = 0; i < 3; i++) {
        const glowAlpha = (3 - i) * 0.1;
        const currentRadius = glowRadius - (i * radius * 0.5);

        ctx.fillStyle = glowColor + Math.floor(glowAlpha * 255).toString(16).padStart(2, '0');
        ctx.beginPath();
        ctx.arc(x, y, currentRadius, 0, Math.PI * 2);
        ctx.fill();
      }

      // Main bead - flat colored circle
      if (isFifthBead) {
        ctx.fillStyle = '#4A9EFF';
      } else {
        ctx.fillStyle = '#FFD700';
      }
    } else {
      // Unscored beads - add subtle border glow for visibility
      ctx.strokeStyle = '#666'; // Dim gray glow
      ctx.lineWidth = 2;
      ctx.shadowColor = '#666';
      ctx.shadowBlur = 4;

      // Draw border first
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.stroke();

      // Reset shadow
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      // Fill with darker color
      ctx.fillStyle = '#111';
    }

    // Draw the main bead circle
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();

    // Add subtle rim only for definition (no 3D effect)
    if (isScored) {
      ctx.strokeStyle = isFifthBead ? '#2B6FCC' : '#CC9900';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    }

    ctx.restore();
  }
}